import { fetch } from './fetchWrapper/fetch';
import { NEXUS_API_URLS, isMockMode, type Environment } from '../constants';
import { mockGet, mockSend } from '../mocks/fixtures';
import type { IApiError } from '../types';

const buildUrl = (environment: Environment, path: string): string => {
  const base = NEXUS_API_URLS[environment];
  if (!base) {
    throw new Error(`No Nexus API URL configured for ${environment}`);
  }
  return `${base}${path}`;
};

const readError = async (response: Response): Promise<IApiError> => {
  let message = response.statusText;
  try {
    const body = await response.json();
    message = body?.message ?? body?.error ?? message;
  } catch {
    // body was not JSON, keep the status text
  }
  return { message, status: response.status };
};

export const apiGet = async <T>(environment: Environment, path: string): Promise<T> => {
  if (isMockMode()) {
    return mockGet<T>(environment, path);
  }
  const response = await fetch(buildUrl(environment, path));
  if (!response.ok) {
    throw await readError(response);
  }
  return response.json() as Promise<T>;
};

export const apiSend = async <T>(
  environment: Environment,
  path: string,
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  body?: unknown,
): Promise<T> => {
  if (isMockMode()) {
    return mockSend<T>(environment, path, method, body);
  }
  const response = await fetch(buildUrl(environment, path), {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) {
    throw await readError(response);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return response.json() as Promise<T>;
};
